/**
 * Readers for a published event's `metadata.sources` (owner ruling R30).
 *
 * Untyped in, typed out, for two independent reasons — so this is the single
 * entry point both render paths go through rather than a convenience:
 *
 * - On the **event edit page** the block arrives through `eventEditSchema`,
 *   whose `metadata` object is `.passthrough()`. Every key not modelled there
 *   — `sources` included — survives the parse as `unknown`, which is exactly
 *   what keeps a curator's save from destroying pipeline-written keys, and
 *   exactly what makes a cast here a lie.
 * - On the **`refresh_review` HITL panel** the entries come off
 *   `hitl_context.problem.sources`, which is typed but still wire data written
 *   by a deploy that may be older than this one.
 *
 * So a malformed entry degrades to "not shown" instead of throwing inside a
 * sidebar, and every consumer sees the same shape.
 *
 * **Two spellings, one reader.** Those two callers do not hold the same keys.
 * Tina's `RefreshSource` is snake_case everywhere inside tina, and
 * `to_patch_dict()` — the one place `seenAt`/`updatedFields` are produced — runs
 * at the Mood PATCH boundary. So `metadata.sources` on a Mood event is
 * camelCase while `hitl_context.problem.sources` is snake_case, and a reader
 * that knew only camelCase quietly dropped half of every HITL entry: the
 * "last compared" line and the updated-field chips never rendered on the very
 * panel R30 added them for. Both spellings are accepted, camelCase first
 * because that is the shape `IEventSource` — the type this returns — declares.
 * Same dual spelling for `tinaItemId`/`tina_item_id` (owner ruling R72): the Tina
 * item that produced/updated the source, not deployed on the Tina side yet, so
 * an entry with neither spelling simply has no trace link.
 */
import type { IEventSource } from "@/types/event";

/**
 * `metadata.sources` as typed entries, dropping anything unrenderable.
 *
 * Accepts either the whole `metadata` block or the `sources` array itself, since
 * the two callers hold different halves: the edit page has the event's metadata,
 * the HITL panel only the list.
 *
 * **`url` is the one required field.** Every entry is rendered as a link, and a
 * link with no href is a row a curator cannot act on — so an entry without one
 * is dropped rather than shown inert. Everything else is optional in the
 * contract and rendered conditionally.
 */
export function readEventSources(value: unknown): IEventSource[] {
  const list = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.sources)
      ? value.sources
      : [];

  return list.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const url = asText(entry.url);
    if (!url) return [];
    const seenAt = asText(entry.seenAt) ?? asText(entry.seen_at);
    const updatedFields = Array.isArray(entry.updatedFields)
      ? entry.updatedFields
      : entry.updated_fields;
    const tinaItemId = asText(entry.tinaItemId) ?? asText(entry.tina_item_id);
    return [
      {
        url,
        ...(asText(entry.site) ? { site: asText(entry.site) } : {}),
        ...(seenAt ? { seenAt } : {}),
        updatedFields: asTextList(updatedFields),
        ...(tinaItemId ? { tinaItemId } : {}),
      } as IEventSource,
    ];
  });
}

/**
 * The host a source URL points at, for a source with no `site` recorded.
 *
 * A bare hostname is a worse label than a Tina site name (`ijland` beats
 * `www.ijland.nl`), which is why `site` wins when present — but it beats
 * printing a 120-character URL as its own label. Returns null for a URL the
 * browser cannot parse, in which case the caller falls back to the raw string.
 */
export function sourceHostLabel(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** `site` if Tina recorded one, else the URL's host, else the URL itself. */
export function sourceLabel(source: IEventSource): string {
  return source.site ?? sourceHostLabel(source.url) ?? source.url;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function asTextList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const text = asText(entry);
    return text ? [text] : [];
  });
}
